import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../SEO';
import { bookSchema } from '../../utils/structuredData';

const PhantomClaimLanding = () => {
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const gtag = (...args) => {
    if (window.gtag) window.gtag(...args);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' ? 'http://localhost:8001' : '');
      if (!backendUrl) throw new Error('Subscription service is currently unavailable.');
      const response = await fetch(`${backendUrl}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          source: 'phantom_claim',
          list_ids: [7], // IRP-Book-Phantom-Claim
          marketing_emails: marketingConsent
        }),
      });

      if (!response.ok) throw new Error('Subscription service is temporarily unavailable.');
      const result = await response.json();
      if (result.success) {
        gtag('event', 'sign_up', {
          method: 'email',
          content_group: 'phantom_claim',
          marketing_consent: marketingConsent,
        });
        setSubmitted(true);
        setEmail('');
      } else {
        throw new Error(result.error || 'Subscription failed.');
      }
    } catch (err) {
      gtag('event', 'form_error', {
        content_group: 'phantom_claim',
        error_message: err.message,
      });
      setError(err.message || 'Unable to subscribe. Please try again later.');
    }
  };

  const bookData = bookSchema({
    title: "The Phantom Claim",
    author: "Evie Montclaire",
    image: "/images/launcher/phantom-claim-cover.png",
    fullDescription: "Some bonds can be rejected. None can be erased. The first book in the Fenmor Pack series — a slow-burn, second-chance paranormal romance that Hits different.",
    releaseDate: "2026-04-20"
  });

  return (
    <div className="min-h-screen bg-[#0a1414] text-white">
      <SEO
        title="The Phantom Claim | Evie Montclaire | Infinite Reyn Productions"
        description="Some bonds can be rejected. None can be erased. The Phantom Claim by Evie Montclaire — available now on Kindle. A slow-burn, second-chance paranormal romance for readers who want their wolf politics sharp and their groveling structural."
        canonical="/launch/the-phantom-claim"
        ogImage="/images/launcher/phantom-claim-cover.png"
        keywords="The Phantom Claim, Evie Montclaire, Fenmor Pack, paranormal romance, fated mates, enemies to lovers, second chance romance, slow burn romance, werewolf romance"
        author="Evie Montclaire"
        structuredData={bookData}
      />
      {/* Section 1 - Hero (Full Screen) */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background with cover palette */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-[#1a2d2d] via-[#0a1414] to-black"
          style={{
            backgroundImage: `url('/images/launcher/phantom-claim-cover.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50"></div>

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-8xl font-light mb-8 leading-tight md:leading-none">
            Some bonds can be rejected.<br className="hidden md:block" />
            None can be erased.
          </h1>
          
          <div className="max-w-2xl mx-auto">
            <p className="text-xl md:text-2xl tracking-wider text-white/80 mb-4 leading-relaxed">
              The Phantom Claim. <span className="text-white">Fenmor Pack Book One.</span>
            </p>
            <p className="text-base md:text-lg text-white/70 mb-8 font-light italic">
              A slow-burn, second-chance paranormal romance — for readers who want their wolf politics sharp and their groveling structural.
            </p>
          </div>
          
          <p className="text-sm tracking-[0.3em] text-gold mb-12">
            FROM <a href="https://www.amazon.com/stores/Evie-Montclaire/author/B0GVWLQD34" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-white transition-colors">EVIE MONTCLAIRE</a> / INFINITE REYN PRODUCTIONS
          </p>

          {/* Email Form */}
          {!submitted ? (
            <div className="max-w-md mx-auto mb-6">
              <form onSubmit={handleSubmit} className="mb-4">
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => gtag('event', 'form_start', { content_group: 'phantom_claim' })}
                    placeholder="Your email"
                    required
                    className="flex-1 bg-white/10 border border-white/20 px-6 py-4 text-white placeholder-white/40 focus:border-gold focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    className="bg-gold text-black px-8 py-4 tracking-wider text-sm font-medium hover:bg-gold/90 transition-colors whitespace-nowrap"
                  >
                    JOIN THE PACK
                  </button>
                </div>
              </form>
              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
              <label className="flex items-start gap-3 cursor-pointer group text-left">
                <div className="relative mt-1">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 border border-white/20 transition-colors ${marketingConsent ? 'bg-gold border-gold' : 'bg-white/5 group-hover:border-gold/50'}`}>
                    {marketingConsent && (
                      <svg className="w-full h-full text-black p-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-[10px] md:text-xs text-white/50 leading-relaxed group-hover:text-white/70 transition-colors">
                  I agree to receive updates and marketing emails from Infinite Reyn Productions. You can unsubscribe at any time.
                </span>
              </label>
            </div>
          ) : (
            <div className="max-w-md mx-auto mb-6 p-6 border border-gold/50 bg-gold/5">
              <p className="text-gold tracking-wider">You're on the list. Watch your inbox.</p>
            </div>
          )}

          <p className="text-xs text-white/40 tracking-widest uppercase">
            Available now on Kindle. No spam, ever.
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-60">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-white/50"></div>
          <span className="text-xs tracking-widest">SCROLL</span>
        </div>
      </section>

      {/* Section 2 - What you get */}
      <section className="py-24 md:py-32 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8 md:space-y-12">
            <div className="pb-8 border-b border-white/10 flex gap-4 md:gap-6 items-start">
              <span className="text-gold text-xl">→</span>
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                Read <span className="text-white font-normal underline decoration-gold/50 underline-offset-4">The Phantom Claim</span> now on Kindle — Fenmor Pack Book One is live.
              </p>
            </div>

            <div className="pb-8 border-b border-white/10 flex gap-4 md:gap-6 items-start">
              <span className="text-gold text-xl">→</span>
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                Step into Vera and Aeden's world — and leave a review that helps new readers find the pack.
              </p>
            </div>

            <div className="pb-8 border-b border-white/10 flex gap-4 md:gap-6 items-start">
              <span className="text-gold text-xl">→</span>
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed">
                First look at everything coming from Infinite Reyn Productions — books, music, theatre — before anyone else.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - The Book */}
      <section className="py-32 px-6 bg-[#0a1414]">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Book Cover */}
            <div className="relative">
              <a href="https://amzn.to/4sRlWOh" target="_blank" rel="noopener noreferrer" onClick={() => gtag('event', 'outbound_click', { content_group: 'phantom_claim', link_target: 'amazon_cover' })}>
                <img
                  src="/images/launcher/phantom-claim-cover.png"
                  alt="The Phantom Claim by Evie Montclaire"
                  className="w-full max-w-md mx-auto shadow-2xl hover:shadow-gold/20 transition-shadow duration-300 cursor-pointer"
                />
              </a>
            </div>

            {/* Book Info */}
            <div className="text-center md:text-left">
              <div className="space-y-6 mb-12">
                <p className="text-lg md:text-xl text-white/60 font-light leading-relaxed">
                  Ten years ago, he chose his pack over their bond.<br />
                  She rebuilt herself entirely. Became a lawyer. Moved three hours south.
                </p>
                <p className="text-lg md:text-xl text-white/60 font-light leading-relaxed">
                  Now her grandmother's death has dragged her back to Fenmor land — and the bond she spent a decade ignoring just woke up.
                </p>
              </div>

              <div className="mb-12">
                <p className="text-3xl md:text-4xl font-light italic mb-4 leading-tight">
                  He rejected their bond in public.
                </p>
                <p className="text-3xl md:text-4xl font-light italic leading-tight">
                  She'll make him earn it in private.
                </p>
              </div>

              {/* Trope Tags */}
              <div className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-2 mb-12 text-[10px] md:text-xs tracking-[0.3em] text-gold/80">
                <span>AGE GAP</span>
                <span>·</span>
                <span>SECOND CHANCE</span>
                <span>·</span>
                <span>ENEMIES TO LOVERS</span>
                <span>·</span>
                <span>FATED MATES</span>
                <span>·</span>
                <span>SLOW BURN</span>
              </div>

              {/* Pull Quote */}
              <blockquote className="border-l-2 border-gold pl-6 mb-12">
                <p className="text-lg text-white/70 font-light italic leading-relaxed mb-4">
                  "She'd spent ten years in Portland being excellent at things that didn't matter here. Fenmor remembered everything."
                </p>
              </blockquote>

              {/* CTA Buttons */}
              <div className="flex flex-col md:flex-row gap-4">
                <a
                  href="https://amzn.to/4sRlWOh"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => gtag('event', 'outbound_click', { content_group: 'phantom_claim', link_target: 'amazon_kindle' })}
                  className="bg-gold text-black px-8 py-4 tracking-wider text-xs md:text-sm font-semibold hover:bg-gold/90 transition-colors whitespace-nowrap text-center"
                >
                  READ ON KINDLE
                </a>
                <a
                  href="https://thefenmorpack.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => gtag('event', 'outbound_click', { content_group: 'phantom_claim', link_target: 'fenmor_pack_community' })}
                  className="border border-white/20 text-white px-8 py-4 tracking-wider text-xs md:text-sm font-semibold hover:border-gold hover:text-gold transition-colors whitespace-nowrap text-center"
                >
                  JOIN THE FENMOR PACK
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - The Imprint */}
      <section className="py-24 px-6 bg-black border-y border-white/10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
          
          <p className="text-lg text-white/70 font-light leading-relaxed mb-6">
            <strong className="text-white font-medium">Infinite Reyn Productions</strong> is a creative house built for stories that refuse to stay in one form. The Phantom Claim is where it starts. Books. Music. Theatre. One world.
          </p>
          
          <p className="text-sm tracking-[0.3em] text-gold">
            WHERE STORIES TAKE FORM
          </p>
        </div>
      </section>

      {/* Section 5 - Songs of Fenmor Soundtrack */}
      <section className="py-24 md:py-32 px-6 bg-[#0a1414]">
        <div className="max-w-3xl mx-auto text-center">
          <img
            src="/images/soundtrack.png"
            alt="Songs of Fenmor — Original Soundtrack"
            className="w-48 md:w-56 mx-auto mb-8"
          />
          <p className="text-xs tracking-[0.3em] text-gold uppercase mb-4">Coming Soon</p>
          <h2 className="text-3xl md:text-4xl font-light mb-4">
            Songs of Fenmor
          </h2>
          <p className="text-lg text-white/60 font-light italic mb-4">
            An Original Soundtrack — Inspired by The Phantom Claim
          </p>
          <p className="text-base text-white/50 font-light">
            Every chapter has a pulse. Every scene has a sound. Streaming soon.
          </p>
        </div>
      </section>

      {/* Section 6 - Final CTA */}
      <section className="py-32 px-6 bg-[#0a1414]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-8">
            The pack is waiting.
          </h2>

          <p className="text-xs text-white/40 tracking-widest uppercase mb-12">
            Available now on Kindle
          </p>

          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <a
              href="https://amzn.to/4sRlWOh"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => gtag('event', 'outbound_click', { content_group: 'phantom_claim', link_target: 'amazon_final_cta' })}
              className="bg-gold text-black px-8 py-4 tracking-wider text-sm font-medium hover:bg-gold/90 transition-colors whitespace-nowrap text-center"
            >
              READ ON KINDLE
            </a>
            <a
              href="https://thefenmorpack.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => gtag('event', 'outbound_click', { content_group: 'phantom_claim', link_target: 'fenmor_pack_final' })}
              className="border border-white/20 text-white px-8 py-4 tracking-wider text-sm font-medium hover:border-gold hover:text-gold transition-colors whitespace-nowrap text-center"
            >
              JOIN THE FENMOR PACK
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-sm text-white/40">
          <Link to="/" className="hover:text-white transition-colors">
            ← Back to Infinite Reyn Productions
          </Link>
          <p>© 2026 Infinite Reyn Productions</p>
        </div>
      </footer>
    </div>
  );
};

export default PhantomClaimLanding;
